import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import * as adminService from '@/redux/services/adminService'
import { RootState } from '@/redux/store'
import { Admin, AdminForm, Group, GroupForm, Setting, SettingForm, Viewer, ViewerForm } from '@/models/admins.model'
import { PostData } from '@/models/ve.model'

interface AdminState {
  admins: Admin[]
  viewers: Viewer[]
  groups: Group[]
  settings: Setting
}

const initialState: AdminState = {
  admins: [],
  viewers: [],
  groups: [],
  settings: {
    id: 0,
    half1_start: '',
    half1_end: '',
    half2_start: '',
    half2_end: '',
    sap_updated_date: '',
    ap_updated_date: '',
    netsuite_updated_date: '',
    vpay_updated_date: '',
    allpay_updated_date: '',
    bpps_ktrd_updated_date: '',
    bpps_kntd_updated_date: '',
    one_time_vendor_updated_date: '',
    cad_updated_date: '',
    product_updated_date: '',
    aws_cost_center_version: 0,
    aws_hierarchy_version: 0,
    aws_mapping_date: '',
    eva_auto_date: '',
    eva_vendor_inactive_date: '',
    eva_vendor_active_date: '',
    updated_by: '',
    updated_at: ''
  }
}

export const getSetting = createAsyncThunk('setting/list', async () => {
  return await adminService.getSetting()
})

export const putUpdateSetting = createAsyncThunk('setting/update', async (data: SettingForm) => {
  return await adminService.putUpdateSetting(data)
})

export const getGroup = createAsyncThunk('group/list', async () => {
  return await adminService.getGroup()
})

export const postCheckGroup = createAsyncThunk('group/check', async (data: PostData) => {
  return await adminService.postCheckGroup(data)
})

export const postCreateGroup = createAsyncThunk('group/create', async (data: GroupForm) => {
  return await adminService.postCreateGroup(data)
})

export const deleteGroup = createAsyncThunk('group/delete', async (data: number) => {
  return await adminService.deleteGroup(data)
})

export const getViewer = createAsyncThunk('viewer/list', async () => {
  return await adminService.getViewer()
})

export const postCheckViewer = createAsyncThunk('viewer/check', async (data: PostData) => {
  return await adminService.postCheckViewer(data)
})

export const postCreateViewer = createAsyncThunk('viewer/create', async (data: ViewerForm) => {
  return await adminService.postCreateViewer(data)
})

export const deleteViewer = createAsyncThunk('viewer/delete', async (data: number) => {
  return await adminService.deleteViewer(data)
})

export const getAdmin = createAsyncThunk('admin/list', async () => {
  return await adminService.getAdmin()
})

export const postCheckAdmin = createAsyncThunk('admin/check', async (data: PostData) => {
  return await adminService.postCheckAdmin(data)
})

export const postCreateAdmin = createAsyncThunk('admin/create', async (data: AdminForm) => {
  return await adminService.postCreateAdmin(data)
})

export const deleteAdmin = createAsyncThunk('admin/delete', async (data: number) => {
  return await adminService.deleteAdmin(data)
})

export const updateProduct = createAsyncThunk('master/product', async () => {
  return await adminService.updateProduct()
})

export const updateAWS = createAsyncThunk('master/hierarchy', async () => {
  return await adminService.updateAWS()
})

export const updateAWSMapping = createAsyncThunk('master/mapping', async () => {
  return await adminService.updateAWSMapping()
})

export const updateKTRD = createAsyncThunk('vendor/ktrd', async () => {
  return await adminService.updateKTRD()
})

export const updateKNTD = createAsyncThunk('vendor/kntd', async () => {
  return await adminService.updateKNTD()
})

export const updateOTV = createAsyncThunk('batch/updateotv', async () => {
  return await adminService.updateOTV()
})

export const updateActive = createAsyncThunk('vendor/active', async () => {
  return await adminService.updateActive()
})

export const updateInactive = createAsyncThunk('vendor/inactive', async () => {
  return await adminService.updateInactive()
})

export const autoP = createAsyncThunk('batch/evaluate', async () => {
  return await adminService.autoP()
})

const adminSlice = createSlice({
  name: 'admin',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getSetting.fulfilled, (state, action) => {
      state.settings = action.payload.result
    })
    builder.addCase(getAdmin.fulfilled, (state, action) => {
      state.admins = action.payload.result
    })
    builder.addCase(getViewer.fulfilled, (state, action) => {
      state.viewers = action.payload.result
    })
    builder.addCase(getGroup.fulfilled, (state, action) => {
      state.groups = action.payload.result
    })
  }
})

export const settingSelector = (store: RootState): Setting => store.admin.settings
export const adminSelector = (store: RootState): Admin[] => store.admin.admins
export const viewerSelector = (store: RootState): Viewer[] => store.admin.viewers
export const groupSelector = (store: RootState): Group[] => store.admin.groups


export default adminSlice.reducer